import { type FC, useEffect, useRef, useState } from "react";
import { ChevronLeft, Info, Mail, PlusCircle } from "lucide-react";

import { PeopleSelector } from "@/shared/components/PeopleSelector";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useUser } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useRequestDocument } from "@/shared/hooks/useRequestDocument";
import { RELATIONSHIP_OPTIONS } from "@/shared/constants/relationshipOptions";
import { getFullName } from "@/shared/utils/formatters";
import { isValidEmail } from "@/shared/utils/validators";

interface RequestDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentLabel: string;
  documentType: string;
  estateId: number;
  firstName: string;
  hint?: string;
  onRequestSent: (recipientName: string) => void;
}

type Step = "select" | "preview";

export const RequestDocumentDialog: FC<RequestDocumentDialogProps> = ({
  open,
  onOpenChange,
  documentLabel,
  documentType,
  estateId,
  firstName,
  hint,
  onRequestSent,
}) => {
  const user = useUser();
  const { contacts, createContactAsync, isCreating } = useUserContacts();
  const requestDocument = useRequestDocument();

  const contactsWithEmail = contacts.filter((c) => c.email && c.email !== user?.email);

  const [step, setStep] = useState<Step>("select");
  const [selectedContactId, setSelectedContactId] = useState<number | "new">(
    contactsWithEmail.length > 0 ? contactsWithEmail[0].id : "new",
  );
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [email, setEmail] = useState("");
  const [personalNote, setPersonalNote] = useState("");
  const [createdContact, setCreatedContact] = useState<{
    id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasSyncedDefault = useRef(false);

  useEffect(() => {
    if (!hasSyncedDefault.current && contactsWithEmail.length > 0) {
      hasSyncedDefault.current = true;
      setSelectedContactId(contactsWithEmail[0].id);
    }
  }, [contactsWithEmail.length]);

  const isNewContact = selectedContactId === "new";

  const selectedContact = !isNewContact
    ? (contactsWithEmail.find((c) => c.id === selectedContactId) ?? createdContact)
    : undefined;

  const canContinue = isNewContact
    ? name.trim() !== "" && isValidEmail(email.trim())
    : !!selectedContact;

  const resetForm = () => {
    setStep("select");
    setName("");
    setRelationship("");
    setEmail("");
    setPersonalNote("");
    setCreatedContact(null);
    setError(null);
    requestDocument.reset();
    setSelectedContactId(contactsWithEmail.length > 0 ? contactsWithEmail[0].id : "new");
  };

  const handleContinue = async () => {
    if (!canContinue || !user) return;
    setError(null);

    if (isNewContact) {
      try {
        const nameParts = name.trim().split(/\s+/);
        const newContact = await createContactAsync({
          first_name: nameParts[0] ?? null,
          last_name: nameParts.length > 1 ? nameParts.slice(1).join(" ") : null,
          email: email.trim(),
          relationship: relationship || null,
        });
        setCreatedContact({
          id: newContact.id,
          email: newContact.email ?? "",
          first_name: newContact.first_name ?? null,
          last_name: newContact.last_name ?? null,
        });
        setSelectedContactId(newContact.id);
        setName("");
        setRelationship("");
        setEmail("");
        setStep("preview");
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to save contact.";
        setError(message);
      }
    } else {
      setStep("preview");
    }
  };

  const handleSendRequest = () => {
    if (!selectedContact?.email || !user) return;
    setError(null);

    const requesterName = [user.first_name, user.last_name].filter(Boolean).join(" ");

    requestDocument.mutate(
      {
        estateId,
        documentType,
        contactId: selectedContact.id,
        notification: {
          recipient_email: selectedContact.email,
          requester_name: requesterName || "Someone",
          inviter_first_name: user.first_name || "Someone",
          invitee_first_name: selectedContact.first_name || "",
          decedent_name: firstName,
          document_label: documentLabel,
          personal_note: personalNote.trim() || undefined,
          user_id: user.user_id,
        },
      },
      {
        onSuccess: () => {
          const sentName = getFullName(selectedContact.first_name, selectedContact.last_name);
          resetForm();
          onOpenChange(false);
          onRequestSent(sentName);
        },
        onError: () => {
          setError("Something went wrong. Please try again.");
        },
      },
    );
  };

  const recipientFirstName = selectedContact?.first_name || "them";
  const recipientFullName = selectedContact
    ? getFullName(selectedContact.first_name, selectedContact.last_name)
    : "";
  const userFirstName = user?.first_name || "Someone";
  const docLabelLower = documentLabel.toLowerCase();

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) resetForm();
        onOpenChange(value);
      }}
    >
      <DialogContent size="sm">
        {step === "select" ? (
          <>
            <DialogHeader>
              <DialogTitle>Who has the {docLabelLower}?</DialogTitle>
              <DialogDescription>
                Add their info and we'll reach out with a secure link so they can upload it
                directly.
              </DialogDescription>
            </DialogHeader>

            <DialogBody>
              <div className="flex flex-col gap-4">
                {contactsWithEmail.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <PeopleSelector
                      mode="single"
                      contacts={contactsWithEmail}
                      value={isNewContact ? null : selectedContactId}
                      onChange={(id) => setSelectedContactId(id ?? "new")}
                    />

                    <button
                      type="button"
                      onClick={() => setSelectedContactId("new")}
                      className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2.5 hover:bg-subtle"
                    >
                      <PlusCircle className="h-5 w-5 shrink-0 text-secondary" />
                      <p className="flex-1 text-left text-sm font-medium">
                        Someone else? Enter their email
                      </p>
                    </button>
                  </div>
                )}

                {isNewContact && (
                  <div className="flex flex-col gap-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium">
                          {contactsWithEmail.length > 0 ? "Full name" : "Name"}
                        </label>
                        <Input
                          placeholder="Their full name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium">Relationship to {firstName}</label>
                        <Select value={relationship} onValueChange={setRelationship}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a relationship" />
                          </SelectTrigger>
                          <SelectContent>
                            {RELATIONSHIP_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium">Email</label>
                      <Input
                        type="email"
                        placeholder="example@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {hint && (
                  <div className="flex gap-3 rounded-lg bg-subtle p-3">
                    <Info className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold">Not sure? That's okay.</p>
                      <p className="text-sm text-secondary">{hint}</p>
                    </div>
                  </div>
                )}

                {error && <FieldError message={error} />}
              </div>
            </DialogBody>

            <Button
              variant="brand"
              className="w-full"
              disabled={!canContinue || isCreating}
              onClick={handleContinue}
            >
              {isCreating ? "Saving..." : "Continue"}
            </Button>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setStep("select")}
                  aria-label="Go back"
                  className="shrink-0 text-tertiary hover:text-primary"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div>
                  <DialogTitle>Here's what {recipientFirstName} will get</DialogTitle>
                  <DialogDescription>Sending to {recipientFullName}</DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <DialogBody>
              <div className="flex flex-col gap-4">
                <div className="rounded-lg border border-primary">
                  <div className="flex items-center gap-2 border-b border-primary bg-subtle px-3 py-2 rounded-t-lg">
                    <Mail className="h-3.5 w-3.5 text-secondary" />
                    <p className="text-2xs text-secondary">
                      What {recipientFirstName} will receive
                    </p>
                  </div>
                  <div className="px-3 py-3 text-sm text-secondary leading-relaxed">
                    <p>Hi {recipientFirstName},</p>
                    <br />
                    <p>
                      {userFirstName} is settling {firstName}'s estate and could use your help.
                      Elayne is keeping everything organized for the family during this time.
                    </p>
                    <br />
                    <p>
                      If you have a copy of {firstName}'s {docLabelLower}, you can upload it
                      securely using the link below. It takes just a minute and goes directly to the
                      estate file.
                    </p>
                    <br />
                    <p className="text-brand underline">
                      Click here to upload {firstName}'s {docLabelLower}
                    </p>
                    <br />
                    <p className="text-2xs text-tertiary">
                      The link will expire in 30 days. Only the document is shared with{" "}
                      {userFirstName}'s estate on Elayne.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium">Add a personal note (optional)</label>
                  <Input
                    placeholder={`e.g. "Dad's attorney should have a copy"`}
                    value={personalNote}
                    onChange={(e) => setPersonalNote(e.target.value)}
                  />
                </div>

                {error && <FieldError message={error} />}
              </div>
            </DialogBody>

            <Button
              variant="brand"
              className="w-full"
              disabled={requestDocument.isPending}
              onClick={handleSendRequest}
            >
              {requestDocument.isPending ? "Sending..." : "Send request"}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
